import { VideoLog } from '@features/video/lib/log';
import { CleanAudio, NoiseSuppressor } from '@features/video/lib/rnnoise';
import { MicIssue } from '@features/video/types';

// Звонок при открытой странице виден только по таймеру
const CHECK_MS = 5000;

type Target = {
  rnnoise: NoiseSuppressor | null;
  // сырой трек на входе шумодава
  raw: () => MediaStreamTrack | null;
  // есть что сторожить: трек опубликован (игрок) или цепочка собрана (источник судьи)
  active: () => boolean;
  micOn: () => boolean;
  onIssue: (issue: MicIssue) => void;
  // цепочка собрана заново: владелец подменяет трек в эфире и индикатор
  onRebuilt: (clean: CleanAudio) => Promise<void>;
  // журнал клиента: лениво, клиент создает сторожа до журнала
  log: () => VideoLog;
};

// Сторож микрофона (по демо rnnoiseRebuild). Прерывание аудиосессии — звонок, другое
// приложение — убивает контекст шумодава молча: state running, а в эфир идет тишина.
// Проверяем по таймеру и при возврате на экран (в фоне таймеры заморожены). Захват
// отобрали (трек ended или muted) — чиним не мы, нужен новый трек от хука устройств.
// Контекст замерз — цепочка заново и подмена трека в публикации; без жеста новый контекст
// может не запуститься, тогда нужен клик. Контекст ждет клика (перезагрузка без клика) —
// не пересобираем, только плашка; клиент на это время мьютит трек в эфире. Выключенный
// микрофон чиним молча: включение — жест, он и разбудит контекст
export class MicWatchdog {
  private timer: number | null = null;
  private repairing = false;
  private issue: MicIssue = null;

  constructor(private readonly target: Target) {}

  start(): void {
    this.stop();
    this.timer = window.setInterval(this.check, CHECK_MS);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.check();
  }

  // Остановка — и при подмене трека (новое устройство, восстановление после звонка): показанную
  // беду снимаем с уведомлением, иначе хук держит плашку, пока следующая проверка не найдет
  // новую
  stop(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    this.set(null);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  // Клик по кнопке или включение микрофона: будим контекст синхронно, пока жив жест
  wake(): void {
    this.target.rnnoise?.wake();
    this.check();
  }

  check = async (): Promise<void> => {
    const { rnnoise } = this.target;
    const raw = this.target.raw();
    if (!raw || !this.target.active() || this.repairing) return;
    this.repairing = true;
    try {
      if (raw.readyState === 'ended' || raw.muted) {
        this.set('lost');
      } else if (!rnnoise?.isAttached) {
        this.set(null);
      } else if (!(await rnnoise.tryResume())) {
        this.set('gesture');
      } else if (await rnnoise.isFrozen()) {
        const clean = await rnnoise.rebuild(raw);
        await this.target.onRebuilt(clean);
        this.target.log().event('mic_rebuilt', { running: rnnoise.isRunning });
        this.set(rnnoise.isRunning ? null : 'gesture');
      } else {
        this.set(null);
      }
    } catch (e) {
      this.target.log().error('mic_repair_failed', e);
      this.set('gesture');
    } finally {
      this.repairing = false;
    }
  };

  private onVisibility = (): void => {
    if (!document.hidden) this.check();
  };

  private set(issue: MicIssue): void {
    const shown = this.target.micOn() ? issue : null;
    if (this.issue === shown) return;
    this.target.log().event('mic_issue', { issue: shown ?? 'none', was: this.issue ?? 'none' });
    this.issue = shown;
    this.target.onIssue(shown);
  }
}
